import { BUSINESS_TOOL_SUB_SECTIONS } from 'constants/businessTool';

import {
  EQUIPMENT_NAMES,
  EQUIPMENT_SECTION_NAMES,
} from 'components/BusinessToolForm/Sections/BusinessOperations/Equipment/utils/form';
import {
  LOCATION_AND_PREMISES_NAMES,
  PREMISES_INFORMATION_NAMES,
} from 'components/BusinessToolForm/Sections/BusinessOperations/LocationAndPremises/utils/form';
import { ASSETS_NAMES } from 'components/BusinessToolForm/Sections/BusinessOperations/OtherAssets/utils/form';
import {
  KEY_SUPPLIERS_CHARACTERIZE,
  PARTNERS_NAMES,
} from 'components/BusinessToolForm/Sections/BusinessOperations/PartnersSection/utils/form';
import { PRODUCTION_NAMES } from 'components/BusinessToolForm/Sections/BusinessOperations/ProductionSection/utils/form';
import { QUALITY_NAMES } from 'components/BusinessToolForm/Sections/BusinessOperations/QualityControl/utils/form';
import {
  STAGES_NAMES,
  TECHNOLOGICAL_PROCESS_NAMES,
} from 'components/BusinessToolForm/Sections/BusinessOperations/TechnologicalProcess/utils/form';
import { WORKING_RESOURCES_NAMES } from 'components/BusinessToolForm/Sections/BusinessOperations/WorkingResources/utils/form';

import { ul } from '../helpers';

const PREMISES_INFORMATION_INSTRUCTIONS = {
  [PREMISES_INFORMATION_NAMES.FILE]: {
    name: '',
    instruction:
      'Fügen Sie ein hochauflösendes Foto des Gebäudes oder der Räumlichkeiten ein. Achten Sie darauf, dass das Bild gut beleuchtet und professionell aufgenommen ist.',
    exampleOne: '',
    exampleTwo: '',
  },
  [PREMISES_INFORMATION_NAMES.ADDRESS]: {
    name: 'Adresse',
    instruction:
      'Geben Sie die vollständige Adresse des Gebäudes oder der Räumlichkeiten an. Fügen Sie Straßenname, Hausnummer, Stadt, Bundesland/Provinz, Postleitzahl und Land hinzu. Stellen Sie sicher, dass die Adresse korrekt und vollständig ist.',
    exampleOne: 'Ton Ave, Des Moines, IA 50313, US',
    exampleTwo: '03 Sylvan Ave, Mountain View, CA 94041',
  },
  [PREMISES_INFORMATION_NAMES.AREA]: {
    name: 'Fläche',
    instruction:
      'Geben Sie die Gesamtfläche des Gebäudes oder der Räumlichkeiten in Quadratmetern, Quadratfuß etc. an.',
    exampleOne: '1,2 Hektar',
    exampleTwo: '750 ft²',
  },
  [PREMISES_INFORMATION_NAMES.TERMS_OF_USE]: {
    name: 'Nutzungsbedingungen',
    instruction:
      'Geben Sie an, ob Sie die Räumlichkeiten mieten oder besitzen. Bei Miete machen Sie bitte Angaben zur Miete. Bei Eigentum nennen Sie den aktuellen Immobilienwert und die Kaufkonditionen.',
    exampleOne: 'Das Gebäude wurde im Januar 2020 für 10 Millionen $ gekauft.',
    exampleTwo: 'Die Räumlichkeiten werden für 5.000 USD pro Monat gemietet.',
  },
  [PREMISES_INFORMATION_NAMES.CURRENT_COST]: {
    name: 'Aktueller Wert',
    instruction: 'Geben Sie den aktuellen Marktwert des Gebäudes oder der Räumlichkeiten an.',
    exampleOne: '',
    exampleTwo: '',
  },
  [PREMISES_INFORMATION_NAMES.PURPOSE]: {
    name: 'Verwendungszweck',
    instruction:
      'Beschreiben Sie den Zweck der Nutzung des Gebäudes/der Räumlichkeiten. Geben Sie an, ob diese gewerblich, wohnwirtschaftlich, industriell oder anderweitig genutzt werden. Beschreiben Sie die dort stattfindenden Aktivitäten, z. B. Büroarbeit, Produktion, Einzelhandel oder Dienstleistungen.',
    exampleOne: 'Produktionsstandort',
    exampleTwo: 'Büroräume in einem Business Center',
  },
};

const LOCATION_AND_PREMISES_INSTRUCTIONS = {
  [LOCATION_AND_PREMISES_NAMES.LOCATION]: {
    name: 'Standort der Räumlichkeiten',
    instruction:
      'Geben Sie einen kurzen Überblick über die Standorte Ihres Unternehmens oder Ihrer Produktionsstätten. Heben Sie wichtige geografische Regionen, Städte oder Länder hervor, in denen sich Ihre Einrichtungen befinden. Nennen Sie, falls zutreffend, die Größe und Kapazität jeder Einrichtung, um einen Eindruck von Ihrer operativen Präsenz zu vermitteln.',
    exampleOne:
      'Die Produktionsstätte des Unternehmens befindet sich strategisch im Herzen des Mittleren Westens, in der Nähe wichtiger Agrarzentren wie Des Moines, Iowa. Dieser Standort gewährleistet Kundennähe und einen einfachen Zugang zu Verkehrsnetzen, was die Vertriebseffizienz optimiert.',
    exampleTwo:
      'Der Hauptsitz befindet sich strategisch im Herzen des Silicon Valley, wodurch das Unternehmen Zugang zu einem großen Pool an Tech-Talenten und Innovationsnetzwerken hat. Außerdem gibt es Satellitenbüros in globalen Schlüsselstandorten wie London und Singapur, um unterschiedliche Märkte effizient zu bedienen.',
  },
  [LOCATION_AND_PREMISES_NAMES.PREMISES_INFORMATION]: PREMISES_INFORMATION_INSTRUCTIONS,
  [LOCATION_AND_PREMISES_NAMES.QUALITIES]: {
    name: 'Standort – Vor- und Nachteile',
    instruction:
      'Geben Sie Parameter wie an:' +
      ul([
        'Mietpreis und Nebenkosten,',
        'Nähe zur Zielkundschaft,',
        'Nähe zu Lieferanten,',
        'niedrige Steuern in der Region,',
        'gute Verkehrsanbindung,',
        'vorhandene saisonale demografische Schwankungen usw.',
      ]),
    exampleOne:
      'Der aktuelle Standort des Unternehmens ist mit hohen Miet- und Nebenkosten verbunden, bietet jedoch Nähe zu Zielkunden und Lieferanten. Die Region zeichnet sich durch niedrige Steuern, gute Verkehrsanbindung und stabile saisonale demografische Verhältnisse aus.',
    exampleTwo:
      'Der aktuelle Standort des Unternehmens bietet wettbewerbsfähige Miet- und Nebenkosten, Nähe zu Zielkunden in Agrarregionen sowie Zugang zu Lieferanten. Allerdings sind die Steuern moderat, und saisonale demografische Schwankungen können die Verfügbarkeit von Arbeitskräften beeinflussen.',
  },
  [LOCATION_AND_PREMISES_NAMES.FUTURE_PLANS]: {
    name: 'Zukünftige Expansion',
    instruction:
      'Gehen Sie näher auf die Pläne für neue Standorte oder Produktionsstätten ein. Erläutern Sie, ob das Unternehmen den Bau neuer Gebäude, die Anmietung bestehender Objekte oder Partnerschaften mit gemeinsam genutzten Einrichtungen in Betracht zieht.',
    exampleOne:
      'Das Unternehmen plant eine Expansion in den Südosten, um aufstrebende Agrarmärkte zu erschließen. Zunächst sollen aus Kostengründen bestehende Flächen angemietet werden, später können Partnerschaften für gemeinsam genutzte Einrichtungen geprüft werden. Diese Expansion zielt darauf ab, die Marktpräsenz zu erweitern und die wachsende Nachfrage in der Region zu nutzen.',
    exampleTwo:
      'Das Unternehmen plant eine Expansion in aufstrebende Märkte wie Lateinamerika und Südostasien und will regionale Büros einrichten, um eine bessere Marktdurchdringung zu erreichen. Die Anmietung bestehender Flächen oder Partnerschaften für gemeinsam genutzte Einrichtungen sind realistische Optionen, die Flexibilität und Kosteneffizienz in der Expansionsstrategie gewährleisten.',
  },
};

const MAIN_EQUIPMENT_INSTRUCTIONS = {
  [EQUIPMENT_SECTION_NAMES.FILE]: {
    name: '',
    instruction:
      'Fügen Sie ein hochauflösendes Foto der Ausrüstung ein. Achten Sie darauf, dass das Bild gut beleuchtet und professionell aufgenommen ist.',
    exampleOne: '',
    exampleTwo: '',
  },
  [EQUIPMENT_SECTION_NAMES.NAME]: {
    name: 'Name der Gruppe',
    instruction:
      'Fassen Sie Ihre Ausrüstung zu einer Gerätegruppe zusammen. Sie können nach Produktionsstufen oder nach Leitausrüstung mit zugehöriger Unterstützungsausrüstung gruppieren.',
    exampleOne: 'Montagelinien-Maschinen',
    exampleTwo: 'Kommunikationsausrüstung',
  },
  [EQUIPMENT_SECTION_NAMES.NUMBER]: {
    name: 'Anzahl der Elemente in der Gruppe',
    instruction: 'Geben Sie die Gesamtzahl der Elemente in der Gerätegruppe an.',
    exampleOne: '11',
    exampleTwo: '6',
  },
  [EQUIPMENT_SECTION_NAMES.COST]: {
    name: 'Aktueller Wert',
    instruction: 'Geben Sie den aktuellen Gesamtwert der Gerätegruppe an.',
    exampleOne: '850 000 USD',
    exampleTwo: '60 000 USD',
  },
  [EQUIPMENT_SECTION_NAMES.PURPOSE]: {
    name: 'Verwendungszweck',
    instruction:
      'Beschreiben Sie die Funktion oder das Ziel der Gerätegruppe. Erläutern Sie die Ergebnisse, die durch den Einsatz erzielt werden, oder ihre Rolle in den operativen Prozessen.',
    exampleOne:
      'Sie ist dafür ausgelegt, den Prozess der Komponenten- und Teilemontage zu automatisieren, die Produktivität zu steigern und Arbeitskosten zu senken. Ihre Rolle besteht darin, einen reibungslosen und sequentiellen Montageablauf sicherzustellen.',
    exampleTwo:
      'Sie ermöglicht nahtlose Interaktion zwischen Teammitgliedern, Kunden und Stakeholdern und unterstützt Echtzeit-Zusammenarbeit sowie verbesserten Kundenservice.',
  },
};

const PURCHASED_EQUIPMENT_INSTRUCTIONS = {
  [EQUIPMENT_SECTION_NAMES.FILE]: {
    name: '',
    instruction:
      'Fügen Sie ein hochauflösendes Foto der Ausrüstung ein. Achten Sie darauf, dass das Bild gut beleuchtet und professionell aufgenommen ist.',
    exampleOne: '',
    exampleTwo: '',
  },
  [EQUIPMENT_SECTION_NAMES.NAME]: {
    name: 'Name der Gruppe',
    instruction: 'Geben Sie den Namen der Ausrüstung oder der Gerätegruppe an, die angeschafft werden soll.',
    exampleOne: 'Materialumschlagsgeräte',
    exampleTwo: 'Datenbankserver',
  },
  [EQUIPMENT_SECTION_NAMES.NUMBER]: {
    name: 'Anzahl der Elemente',
    instruction: 'Geben Sie die Gesamtzahl der Elemente in der Gerätegruppe an.',
    exampleOne: '13',
    exampleTwo: '20',
  },
  [EQUIPMENT_SECTION_NAMES.COST]: {
    name: 'Geplanter Wert',
    instruction: 'Geben Sie den geplanten Gesamtwert der Gerätegruppe an.',
    exampleOne: '8 Mio. USD',
    exampleTwo: '80 000 USD',
  },
  [EQUIPMENT_SECTION_NAMES.PURPOSE]: {
    name: 'Verwendungszweck',
    instruction:
      'Beschreiben Sie die Funktion oder das Ziel der neuen Ausrüstung. Erläutern Sie die Ergebnisse, die durch den Einsatz erzielt werden, oder ihre Rolle in den operativen Prozessen.',
    exampleOne:
      'Sie steigert die operative Effizienz, indem sie den manuellen Arbeitsaufwand reduziert. Die Ausrüstung sorgt für einen reibungslosen Workflow und eine termingerechte Lieferung von Komponenten und trägt so zu Produktivität und Kosteneffizienz bei.',
    exampleTwo:
      'Sie stellt ein zentrales Repository für die Speicherung kritischer Informationen bereit und gewährleistet Datenintegrität, Sicherheit und Zugriffsmöglichkeiten.',
  },
};

const EQUIPMENT_INSTRUCTIONS = {
  [EQUIPMENT_NAMES.DESCRIPTION]: {
    name: 'Ausrüstungsnutzung',
    instruction:
      'Geben Sie einen prägnanten Überblick über die Ausrüstung, über die Ihr Unternehmen derzeit verfügt. Dazu gehören Maschinen, Werkzeuge, Technologie und andere für Ihren Betrieb wichtige Vermögenswerte. Nennen Sie klar die Typen, Modelle und Kapazitäten der Ausrüstung und geben Sie damit einen Einblick in die Mechanismen, die Ihre Geschäftsprozesse antreiben.',
    exampleOne:
      'Die Produktionslinie des Unternehmens ist mit modernsten Maschinen ausgestattet, darunter CNC-Bearbeitungszentren, robotergestützte Schweißstationen und Präzisionsmontagewerkzeuge. Diese Ausstattung ermöglicht die Fertigung spezialisierter Komponenten und die Montage von Kettentraktoren mit patentierter Endlosketten-Technologie, was höchste Qualität und Effizienz in der Produktion sicherstellt.',
    exampleTwo:
      'Die Ausrüstung des Unternehmens umfasst hochmoderne Übersetzungssoftware, einschließlich CAT-Tools wie SDL Trados und memoQ, unterstützt durch Hochgeschwindigkeitsserver für die Datenverarbeitung. Das Unternehmen nutzt außerdem fortschrittliche KI-Algorithmen zur Qualitätssicherung, um die Übersetzungsgenauigkeit und Effizienz zu steigern.',
  },
  [EQUIPMENT_NAMES.MAIN_EQUIPMENT]: MAIN_EQUIPMENT_INSTRUCTIONS,
  [EQUIPMENT_NAMES.LEVEL_OF_UTILIZATION]: {
    name: 'Auslastungsgrad',
    instruction:
      'Beschreiben Sie den Auslastungsgrad der Ausrüstung mit Fokus auf Effizienz und Optimierung. Geben Sie Informationen zu Nutzungsmustern, Strategien zur Reduktion von Stillstandszeiten und Wartungsplänen an.',
    exampleOne:
      'Die Ausrüstung des Unternehmens arbeitet mit maximaler Effizienz; die Auslastung liegt konstant über 90 %. Präventive Wartungspläne minimieren Stillstandszeiten und optimieren die Leistung. Kontinuierliches Monitoring und Datenanalyse treiben Effizienzverbesserungen voran und sichern reibungslose Produktionsprozesse.',
    exampleTwo:
      'Die Ausrüstung des Unternehmens arbeitet mit hoher Effizienz und erreicht im Durchschnitt einen Auslastungsgrad von 90 %. Stillstandszeiten werden durch proaktive Wartungspläne und prädiktive Analysen minimiert. Nutzungsmuster werden laufend ausgewertet, um die Produktivität zu maximieren und Unterbrechungen zu reduzieren.',
  },
  [EQUIPMENT_NAMES.PURCHASED_EQUIPMENT]: PURCHASED_EQUIPMENT_INSTRUCTIONS,
  [EQUIPMENT_NAMES.RATIONALE_FOR_PURCHASING]: {
    name: 'Begründung für Anschaffung',
    instruction:
      'Begründen Sie die Anschaffung der Ausrüstung, indem Sie Effizienzgewinne und Kosteneinsparungen hervorheben. Erläutern Sie, wie die Ausrüstung zu den Produktionsanforderungen passt und die Wachstumsziele des Unternehmens unterstützt.',
    exampleOne:
      'Das Unternehmen investiert in modernste Ausrüstung, um Effizienz zu steigern und langfristige Kosten zu senken. Die Modernisierung stellt sicher, dass die Produktionskapazitäten mit den sich wandelnden Anforderungen Schritt halten, und steigert Leistungsfähigkeit und Qualität. Diese strategische Investition beschleunigt das Wachstum des Unternehmens und stärkt die Wettbewerbsposition am Markt.',
    exampleTwo:
      'Die Investition in moderne Übersetzungssoftware und KI-Algorithmen steht im Einklang mit dem Ziel des Unternehmens, Effizienz zu steigern und operative Kosten zu reduzieren. Diese Tools straffen Prozesse, gewährleisten die termingerechte Lieferung hochwertiger Übersetzungen und unterstützen die Skalierbarkeit bei steigender Nachfrage.',
  },
};

const GROUPS_INSTRUCTIONS = {
  [EQUIPMENT_SECTION_NAMES.FILE]: {
    name: '',
    instruction:
      'Fügen Sie ein hochauflösendes Foto des Vermögenswerts ein. Achten Sie darauf, dass das Bild gut beleuchtet und professionell aufgenommen ist.',
    exampleOne: '',
    exampleTwo: '',
  },
  [EQUIPMENT_SECTION_NAMES.NAME]: {
    name: 'Name der Gruppe',
    instruction: 'Geben Sie die genaue Bezeichnung der Vermögenswertgruppe an.',
    exampleOne: 'Serviceausrüstung',
    exampleTwo: 'Linguistische Datenbanken',
  },
  [EQUIPMENT_SECTION_NAMES.NUMBER]: {
    name: 'Anzahl der Elemente',
    instruction: 'Geben Sie die Gesamtzahl der Elemente in der Vermögenswertgruppe an.',
    exampleOne: '9',
    exampleTwo: '3',
  },
  [EQUIPMENT_SECTION_NAMES.COST]: {
    name: 'Aktueller Wert',
    instruction: 'Geben Sie den aktuellen Gesamtwert der Vermögenswertgruppe an.',
    exampleOne: '1 500 000 $',
    exampleTwo: '300 000 $',
  },
  [EQUIPMENT_SECTION_NAMES.PURPOSE]: {
    name: 'Verwendungszweck',
    instruction:
      'Erläutern Sie, wie die Vermögenswertgruppe zur Geschäftstätigkeit, zum Wachstum oder zu den Zielen des Unternehmens beiträgt. Beschreiben Sie ihre Bedeutung und ihren Nutzen, einschließlich Auswirkungen auf Produktivität, Effizienz, Ertragsgenerierung und Wachstumsstrategie.',
    exampleOne:
      'Diese Vermögenswerte ermöglichen eine effiziente Wartung und Reparatur der Traktoren des Unternehmens. Sie sorgen für zeitnahe Eingriffe, erhöhen die Lebensdauer und Leistungsfähigkeit der Traktoren und stärken damit Kundenzufriedenheit und -bindung.',
    exampleTwo:
      'Diese Datenbanken speichern linguistische Daten, darunter Fachterminologie und Phrasen, die für präzise Übersetzungs- und Lokalisierungsprojekte erforderlich sind. Sprachprofis nutzen sie, um kulturelle und sprachliche Lücken zu überbrücken und eine klare, kulturell sensible Kommunikation sicherzustellen.',
  },
};

const PURCHASED_GROUPS_INSTRUCTIONS = {
  [EQUIPMENT_SECTION_NAMES.FILE]: {
    name: '',
    instruction:
      'Fügen Sie ein hochauflösendes Foto des Vermögenswerts ein. Achten Sie darauf, dass das Bild gut beleuchtet und professionell aufgenommen ist.',
    exampleOne: '',
    exampleTwo: '',
  },
  [EQUIPMENT_SECTION_NAMES.NAME]: {
    name: 'Name der Gruppe',
    instruction: 'Geben Sie die genaue Bezeichnung der Vermögenswertgruppe an.',
    exampleOne: 'Satellitenbildgebungssysteme',
    exampleTwo: 'KI-gestützte Übersetzungstools',
  },
  [EQUIPMENT_SECTION_NAMES.NUMBER]: {
    name: 'Anzahl der Elemente',
    instruction: 'Geben Sie die Gesamtzahl der Elemente in der Vermögenswertgruppe an.',
    exampleOne: '3',
    exampleTwo: '2',
  },
  [EQUIPMENT_SECTION_NAMES.COST]: {
    name: 'Geplanter Wert',
    instruction: 'Geben Sie den geplanten Gesamtwert der Vermögenswertgruppe an.',
    exampleOne: '1 000 000 $',
    exampleTwo: '100 000 $',
  },
  [EQUIPMENT_SECTION_NAMES.PURPOSE]: {
    name: 'Verwendungszweck',
    instruction:
      'Geben Sie den Zweck der geplanten Anschaffung an und präzisieren Sie, welche Aufgaben oder Ziele mit diesen Vermögenswerten erreicht werden sollen.',
    exampleOne:
      'Bereitstellung von Echtzeitdaten zu Feldbedingungen, die eine präzise Überwachung der Pflanzengesundheit, des Bodenfeuchtigkeitsniveaus und des gesamten Flächenmanagements ermöglichen, um Produktivität und Ressourceneffizienz zu steigern.',
    exampleTwo:
      'Die Plattform ermöglicht eine schnellere und zuverlässigere Kommunikation in mehreren Sprachen für Kunden und Übersetzer, indem sie die Effizienz und Genauigkeit der Übersetzungsprozesse verbessert.',
  },
};

const ASSETS_INSTRUCTIONS = {
  [ASSETS_NAMES.DESCRIPTION]: {
    name: 'Anlagevermögen',
    instruction:
      'Identifizieren Sie die wichtigsten Anlagewerte wie Grundstücke, biologische Vermögenswerte, Transportmittel und immaterielle Vermögenswerte. Geben Sie prägnante Beschreibungen für jede Kategorie an, um eine umfassende Darstellung sicherzustellen.',
    exampleOne: 'Lastwagen. 5 Lastwagen liefern die erforderlichen Teile und Rohstoffe.',
    exampleTwo:
      'Dienstwagen. Manager nutzen die Fahrzeuge, um zu Treffen mit Lieferanten, Investoren oder Partnern zu fahren.',
  },
  [ASSETS_NAMES.GROUPS]: GROUPS_INSTRUCTIONS,
  [ASSETS_NAMES.PURCHASED_GROUPS]: PURCHASED_GROUPS_INSTRUCTIONS,
  [ASSETS_NAMES.RATIONALE_FOR_PURCHASING]: {
    name: 'Begründung für Erwerb',
    instruction:
      'Erläutern Sie die strategische Bedeutung der Vermögensanschaffungen und heben Sie Wertzuwachs und betriebliche Vorteile hervor. Beschreiben Sie, wie diese Vermögenswerte zur Effizienz, Produktivität oder zu Wettbewerbsvorteilen des Unternehmens beitragen.',
    exampleOne:
      'Die Anschaffung zusätzlicher Vermögenswerte wie fortschrittlicher Software verbessert die Datenanalyse und strafft Abläufe, wodurch Effizienz und Entscheidungsfindung gesteigert werden. Diese Investitionen erhöhen die Produktivität und stärken die Wettbewerbsposition des Unternehmens am Markt, was den Weg für nachhaltiges Wachstum und Innovation ebnet.',
    exampleTwo:
      'Die Anschaffung zusätzlicher Vermögenswerte wie Hochgeschwindigkeitsserver und fortschrittlicher Datensicherheitssysteme verbessert die operative Effizienz und stärkt die Wettbewerbsposition des Unternehmens. Diese Investitionen erhöhen die Datenverarbeitungskapazitäten, gewährleisten reibungslose Abläufe und schützen sensible Informationen, was letztlich das Unternehmenswachstum fördert.',
  },
};

const STAGES_INSTRUCTIONS = {
  [STAGES_NAMES.STAGE]: {
    name: 'Prozessphasen',
    instruction:
      'Skizzieren Sie die wichtigsten Phasen des Produktionsprozesses. Geben Sie einen schrittweisen Überblick über die Erstellung Ihres Produkts oder Ihrer Dienstleistung. Beschreiben Sie jede Phase kurz, um Einblick in den technologischen Prozess zu geben.',
    exampleOne:
      'Komponentenfertigung: Herstellung einzelner Teile wie Fahrgestell, Motorkomponenten, Getriebesysteme und Kettenbaugruppen.',
    exampleTwo:
      'Anfrageeinreichung: Kunden übermitteln Übersetzungsanfragen und geben Sprachpaare, Fristen und Projektdetails an.',
  },
};

const TECHNOLOGICAL_PROCESS_INSTRUCTIONS = {
  [TECHNOLOGICAL_PROCESS_NAMES.TECHNOLOGY]: {
    name: 'Produktionstechnologie',
    instruction:
      'Beschreiben Sie die derzeit eingesetzte Technologie in Produktion oder Dienstleistungserbringung. Betonen Sie Effizienzgewinne, Qualitätsverbesserungen und Wettbewerbsvorteile.',
    exampleOne:
      'Die aktuelle Produktionstechnologie nutzt patentierte Nano-Gummi-Technologie. Diese Innovation, angewendet auf das kontinuierliche Kettendesign des Unternehmens, reduziert Bodendruck und -einwirkung, steigert die landwirtschaftliche Effizienz und verschafft einen Wettbewerbsvorteil.',
    exampleTwo:
      'Die Dienstleistungserbringung stützt sich auf modernste Übersetzungssoftware, die von KI-Algorithmen betrieben wird und unübertroffene Genauigkeit und Effizienz gewährleistet. Diese Technologie ermöglicht es dem Unternehmen, hochwertige Übersetzungen zeitnah zu liefern, die Wettbewerbsfähigkeit zu sichern und die Kundenzufriedenheit zu steigern.',
  },
  [TECHNOLOGICAL_PROCESS_NAMES.QUALITIES]: {
    name: 'Vorteile/Nachteile',
    instruction:
      'Beschreiben Sie die Vorteile der aktuellen Technologie mit Fokus auf Effizienz, Qualität und Wettbewerbsvorteil. Gehen Sie auch auf Einschränkungen wie Kosten, Lernkurve oder Abhängigkeit von Infrastruktur ein.',
    exampleOne:
      'Die Technologie ermöglicht eine bessere Traktion, geringeren Bodendruck, verbesserte Auftriebseigenschaften und höhere landwirtschaftliche Effizienz. Allerdings sind die Produktionskosten höher, die Verschleißfestigkeit geringer und der Einsatz außerhalb der Landwirtschaft begrenzt.',
    exampleTwo: '',
  },
  [TECHNOLOGICAL_PROCESS_NAMES.STAGES]: STAGES_INSTRUCTIONS,
  [TECHNOLOGICAL_PROCESS_NAMES.COMPETING_TECHNOLOGY]: {
    name: 'Konkurrenztechnologien',
    instruction:
      'Geben Sie einen Überblick über die technologische Landschaft Ihrer Branche. Identifizieren Sie Schlüsseltechnologien, die derzeit von Wettbewerbern oder in aufstrebenden Märkten genutzt werden. Erläutern Sie, wie diese konkurrierenden Technologien Ihr Produkt oder Ihre Dienstleistung beeinflussen könnten, und analysieren Sie deren Stärken und Schwächen.',
    exampleOne:
      'Konkurrenztechnologien wie autonome Fahrzeuge und Elektrotraktoren stellen potenzielle Bedrohungen dar. Obwohl sie Fortschritte in Effizienz und Nachhaltigkeit bieten, können hohe Anschaffungskosten und Akzeptanzbarrieren die Dominanz des Unternehmens im Traktorenmarkt herausfordern.',
    exampleTwo:
      'Konkurrenztechnologien wie maschinelle Übersetzung, Sprachverarbeitungs-APIs und Crowdsourcing-Plattformen können den Marktanteil des Unternehmens beeinflussen. Zwar bieten sie Automatisierung und Skalierbarkeit, erreichen jedoch möglicherweise nicht die Präzision und Qualitätssicherung menschlicher Übersetzer.',
  },
};

const WORKING_RESOURCES_INSTRUCTIONS = {
  [WORKING_RESOURCES_NAMES.NECESSARY_RECOURSES]: {
    name: 'Materialbedarf',
    instruction:
      'Geben Sie einen prägnanten Überblick über die wesentlichen Ressourcen, die für Ihren Produktionsprozess benötigt werden. Gliedern Sie die Hauptkategorien wie Rohstoffe, Materialien und Komponenten. Erläutern Sie die zentrale Rolle dieser Ressourcen im Produktionszyklus und betonen Sie ihre Bedeutung für einen reibungslosen Fertigungsprozess.',
    exampleOne:
      'Das Unternehmen benötigt für die Produktion Gummi, Kunststoffe und Stahl. Ebenfalls erforderlich sind Komponenten wie Ketten, Fahrgestelle, Motoren, Getriebe, Hydraulikzylinder, Karosserien, Kühlsysteme, Fahrerstände, Kraftstoff- und elektrische Systeme.',
    exampleTwo:
      'Die notwendigen materiellen Ressourcen umfassen Bürobedarf wie Schreibwaren, Kaffee, Tee, Wasser und Einweggeschirr. Diese Artikel sind unerlässlich für die Aufrechterhaltung eines angenehmen Arbeitsumfelds für die Mitarbeiter.',
  },
  [WORKING_RESOURCES_NAMES.AVAILABILITY]: {
    name: 'Ressourcenstabilität',
    instruction:
      'Beschreiben Sie Verfügbarkeit und Stabilität der Ressourcen und heben Sie Zuverlässigkeit und Kontinuität hervor. Erläutern Sie Bestands- und Vorsorgepläne für mögliche Störungen, um unterbrechungsfreien Betrieb sicherzustellen.',
    exampleOne:
      'Die Versorgung mit Ressourcen wird kontinuierlich auf Stabilität und Verfügbarkeit überwacht. Unternehmen unterhalten diversifizierte Lieferantennetzwerke, um Störungen zu minimieren. Bestandspläne beinhalten Sicherheitsbestände und alternative Beschaffungsstrategien, um den Betrieb auch bei potenziellen Unterbrechungen aufrechtzuerhalten.',
    exampleTwo:
      'Zuverlässige Lieferanten und diversifizierte Beschaffung gewährleisten Verfügbarkeit und Stabilität der Ressourcen. Der Lieferant des Unternehmens hat seine Zuverlässigkeit in den letzten vier Jahren unter Beweis gestellt. Die kontinuierliche Beobachtung von Wettbewerbern ermöglicht schnelle Anpassungen bei unvorhergesehenen Ereignissen.',
  },
};

const KEY_SUPPLIERS_INSTRUCTIONS = {
  [KEY_SUPPLIERS_CHARACTERIZE.NAME]: {
    name: 'Name des Lieferanten',
    instruction: 'Geben Sie den vollständigen Namen des Lieferanten an und fügen Sie relevante Details hinzu.',
    exampleOne: 'Steelnow',
    exampleTwo: 'VPS',
  },
  [KEY_SUPPLIERS_CHARACTERIZE.PRODUCT]: {
    name: 'Dienstleistung/Produkt',
    instruction: 'Geben Sie den Namen des gelieferten Produkts oder der Dienstleistung an.',
    exampleOne: 'Hochfester niedriglegierter Stahl',
    exampleTwo: 'Büromaterial: Schreibgeräte, Papier und Blöcke, Ablage- und Aufbewahrungssysteme',
  },
  [KEY_SUPPLIERS_CHARACTERIZE.QUANTITY]: {
    name: 'Menge pro Monat',
    instruction:
      'Geben Sie die monatliche Menge des vom Lieferanten bereitgestellten Produkts oder der Dienstleistung an, einschließlich der relevanten Einheiten.',
    exampleOne: '1,5 t',
    exampleTwo: 'Insgesamt 50 Einheiten',
  },
  [KEY_SUPPLIERS_CHARACTERIZE.PRICE]: {
    name: 'Preis',
    instruction: 'Geben Sie den Preis des vom Lieferanten angebotenen Produkts oder der Dienstleistung genau an.',
    exampleOne: '500 USD pro Tonne',
    exampleTwo: '300 USD',
  },
  [KEY_SUPPLIERS_CHARACTERIZE.DELIVERY_TERMS]: {
    name: 'Lieferbedingungen',
    instruction: 'Geben Sie die Lieferbedingungen kurz an, z. B. Vorkasse, Zahlung bei Lieferung oder Zahlungsziel.',
    exampleOne: '',
    exampleTwo: '',
  },
};

const PARTNERS_INSTRUCTIONS = {
  [PARTNERS_NAMES.KEY_SUPPLIERS]: KEY_SUPPLIERS_INSTRUCTIONS,
  [PARTNERS_NAMES.CRITERIA]: {
    name: 'Auswahlkriterien',
    instruction:
      'Skizzieren Sie die spezifischen Kriterien und Überlegungen, die Ihren Lieferantenauswahlprozess leiten. Gehen Sie auf Faktoren wie günstige Preisgestaltung, logistische Vorteile, flexible Kreditkonditionen und besondere Rohstoffqualität ein. Erläutern Sie klar die Bedeutung jedes Kriteriums im Kontext Ihrer Geschäftsstrategie und -ziele.',
    exampleOne:
      'Bei der Lieferantenauswahl berücksichtigt das Unternehmen die Qualität der Rohstoffe und Komponenten, die für die Langlebigkeit der Traktoren entscheidend ist. Zuverlässige Logistik gewährleistet die pünktliche Lieferung von Komponenten und Materialien, was für unsere Produktionsprozesse von größter Bedeutung ist.',
    exampleTwo:
      'Bei der Lieferantenauswahl legt das Unternehmen Wert auf wettbewerbsfähige Preise für Kosteneffizienz, ein zuverlässiges System für unterbrechungsfreie Leistungen und flexible Kreditkonditionen für finanzielle Stabilität.',
  },
};

const PRODUCTION_INSTRUCTIONS = {
  [PRODUCTION_NAMES.SPECIFICS]: {
    name: 'Produktion – Besonderheiten',
    instruction:
      'Beschreiben Sie saisonale Arbeitspläne und gehen Sie auf Nachfrageschwankungen ein. Heben Sie Pläne zur Optimierung der Hochsaison und Anpassungen der Arbeitszeiten hervor. Berücksichtigen Sie regulatorische oder externe Abhängigkeiten, die Produktionszeiträume beeinflussen.',
    exampleOne:
      'Die Produktion läuft ganzjährig; in Spitzenzeiten wie Frühling und Herbst setzt das Unternehmen verlängerte Arbeitszeiten und zusätzliche Schichten ein, um die erhöhte Nachfrage zu decken. Die Einsatzpläne werden entsprechend angepasst und bleiben flexibel, um Schwankungen aufzufangen.',
    exampleTwo:
      'Die Geschäftstätigkeit des Unternehmens ist auf saisonale Nachfrage abgestimmt; Spitzenzeiten fallen mit akademischen Semestern und internationalen Veranstaltungen zusammen. Flexible Arbeitszeiten werden eingeführt, um diese Schwankungen zu berücksichtigen.',
  },
  [PRODUCTION_NAMES.PACKAGE]: {
    name: 'Verpackung',
    instruction:
      'Beschreiben Sie die Verpackungsstrategie mit Fokus auf Produktschutz und Markenauftritt. Gehen Sie auf Materialien, Design und gegebenenfalls umweltfreundliche Aspekte ein.',
    exampleOne:
      'Je nach Transportmethode können die Traktoren auf Plattformen oder in Containern verladen werden. Sie werden mit Gurten oder Ketten gesichert, um Bewegungen während des Transports zu verhindern. Zusätzliche Materialien können eingesetzt werden, um empfindliche Teile des Traktors vor Schäden zu schützen.',
    exampleTwo:
      'Die Verpackungsstrategie des Unternehmens zielt in erster Linie auf Produktschutz ab. Um die Umweltbelastung zu minimieren, verwendet das Unternehmen langlebige und umweltfreundliche Materialien. Das Design integriert die Unternehmensfarben und Logos zur besseren Wiedererkennung.',
  },
  [PRODUCTION_NAMES.INVENTORY_MANAGEMENT]: {
    name: 'Lagerverwaltung',
    instruction:
      'Geben Sie einen Überblick über das Lagerverwaltungssystem des Unternehmens. Nennen Sie die Software oder das Programm, das für eine effektive Bestandskontrolle verwendet wird. Beschreiben Sie die wichtigsten Funktionen des Systems, wie Echtzeitverfolgung, Auftragsverwaltung und Integration mit anderen Geschäftsprozessen.',
    exampleOne:
      'Das Unternehmen nutzt ERP-Software für eine integrierte Lagerverwaltung, die eine Echtzeitverfolgung von Bestellungen und die Integration mit anderen Geschäftsprozessen wie Produktion und Vertrieb ermöglicht. Das System stellt optimale Bestände sicher, um die Nachfrage zu decken und Lagerkosten zu minimieren.',
    exampleTwo:
      'Das Unternehmen verwendet ein cloudbasiertes Lagerverwaltungssystem, das eine Echtzeitverfolgung von Vorgängen und eine nahtlose Integration mit anderen Services ermöglicht, um einen reibungslosen Systembetrieb sicherzustellen.',
  },
  [PRODUCTION_NAMES.OUTSOURCING_OPPORTUNITIES]: {
    name: 'Outsourcing-Möglichkeiten',
    instruction:
      'Geben Sie einen prägnanten Überblick über die Outsourcing-Möglichkeiten in Ihren Produktionsprozessen. Erläutern Sie, welche konkreten Phasen oder Aspekte ausgelagert werden könnten und welche Vorteile sich daraus ergeben. Heben Sie strategische Gründe für Outsourcing hervor, z. B. Kosteneffizienz, Skalierbarkeit oder Zugang zu spezialisiertem Know-how.',
    exampleOne:
      'Das Unternehmen lagert die Komponentenfertigung, Montage und Logistik der Distribution aus. Outsourcing bietet Kostenvorteile, Skalierbarkeit und Zugang zu spezialisiertem Fachwissen.',
    exampleTwo:
      'Das Unternehmen lagert Kundensupport und Content-Erstellung aus, wodurch die Belastung der Personalabteilung reduziert und Projektaufgaben ohne formelle Einstellung zusätzlichen Personals umgesetzt werden können.',
  },
  [PRODUCTION_NAMES.INVOLVED_PRODUCTION]: {
    name: 'Einbindung Dritter',
    instruction:
      'Geben Sie an, welche Zwischenstufen oder konkreten Aspekte der Produktion an externe Partner vergeben werden. Begründen Sie diese Entscheidung kurz und heben Sie die strategischen Vorteile oder Effizienzgewinne hervor, die sich aus der Zusammenarbeit mit Dritten ergeben.',
    exampleOne:
      'Die Zusammenarbeit mit Drittunternehmen ist notwendig, um spezialisierte Komponenten wie Hydrauliksysteme und Motoren zu fertigen.',
    exampleTwo:
      'Drittdienstleister werden eingesetzt, um sprachliche Genauigkeit und kulturelle Sensibilität im Produktionsprozess sicherzustellen. Diese Zusammenarbeit verbessert die Qualität, fördert Vielfalt und beschleunigt den Projektabschluss.',
  },
  [PRODUCTION_NAMES.DISPOSAL_WAIST]: {
    name: 'Abfallmanagement',
    instruction:
      'Erläutern Sie die Verfahren zur Abfallentsorgung und betonen Sie Nachhaltigkeit und Einhaltung von Vorschriften. Beschreiben Sie Sortiermethoden, Recyclingmaßnahmen und Kooperationen mit Entsorgungsdienstleistern.',
    exampleOne:
      'Vor Ort erfolgt eine Abfallsortierung zur Wiederverwertung oder sicheren Entsorgung. Recycelbare Materialien wie Metall, Kunststoff und Papier werden getrennt. Gefährliche Stoffe werden über zertifizierte Partner entsorgt. Die Einhaltung von Vorschriften und Nachhaltigkeit im Abfallmanagement werden durch kontinuierliche Überwachung sichergestellt.',
    exampleTwo:
      'Elektronische Altgeräte werden gemäß internationalen Standards recycelt, um Nachhaltigkeit und Regelkonformität zu gewährleisten. Das Unternehmen arbeitet mit spezialisierten Entsorgungsdienstleistern zusammen, um eine sachgerechte Sortierung und Wiederverwertung sicherzustellen und die Umweltbelastung zu minimieren.',
  },
};

const QUALITY_INSTRUCTIONS = {
  [QUALITY_NAMES.FACTORS]: {
    name: 'Qualitätsfaktoren',
    instruction:
      'Identifizieren und erläutern Sie die wichtigsten Faktoren, die die Qualität des Endprodukts beeinflussen. Dazu können Rohstoffe, Fertigungsprozesse, Präzision der Ausrüstung und die Einhaltung von Branchenstandards gehören.',
    exampleOne:
      'Wesentliche Faktoren, die die Produktqualität beeinflussen, sind die Qualität der Rohstoffe und die Genauigkeit der Montage. Die Rohstoffqualität bestimmt direkt die Haltbarkeit, während präzise Montage Zuverlässigkeit und Langlebigkeit sichert.',
    exampleTwo:
      'Wesentliche Faktoren für die Produktqualität sind präzise Übersetzung, kulturelle Sensibilität, die Einhaltung von Kundenvorgaben und eine effektive Kommunikation zwischen Übersetzern und Kunden. Qualitätssicherungsprozesse und Feedbackmechanismen gewährleisten zusätzlich gleichbleibend hohe Ergebnisse.',
  },
  [QUALITY_NAMES.METHODS]: {
    name: 'Kontrollmethoden',
    instruction:
      'Beschreiben Sie die wichtigsten Qualitätskontrollmethoden, die in der Fertigung oder Dienstleistungserbringung eingesetzt werden. Gehen Sie auf Prüfprotokolle, Testverfahren oder Kontrollpunkte im Produktionszyklus ein, die zur Sicherstellung der Konsistenz und Zuverlässigkeit des Endprodukts dienen.',
    exampleOne:
      'Qualitätskontrollmethoden umfassen strenge Prüfung der Rohstoffe, regelmäßige Tests von Komponenten und umfassende Kontrollen während der Montage. Protokolle für Maßhaltigkeit, Festigkeit und Leistungsfähigkeit werden in jeder Phase befolgt, um Konsistenz und Zuverlässigkeit sicherzustellen.',
    exampleTwo:
      'Zentrale Qualitätskontrollmethoden sind gründliche Dokumentenprüfung, der Einsatz automatisierter Übersetzungstools und menschliches Lektorat. Regelmäßige Audits, Kundenfeedback und Leistungsbewertungen sichern über den gesamten Dienstleistungsprozess hinweg ein konstantes Qualitätsniveau.',
  },
};

export const BUSINESS_OPERATIONS_INSTRUCTIONS = {
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.LOCATION_AND_PREMISES]: LOCATION_AND_PREMISES_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.EQUIPMENT]: EQUIPMENT_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.ASSETS]: ASSETS_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.TECHNOLOGICAL_PROCESS]: TECHNOLOGICAL_PROCESS_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.WORKING_RESOURCES]: WORKING_RESOURCES_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.PARTNERS]: PARTNERS_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.PRODUCTION]: PRODUCTION_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.QUALITY]: QUALITY_INSTRUCTIONS,
};
